// React
import { useEffect, useMemo, useRef, useState } from "react";

// Libraries
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

// Material UI
import { Box, Button, Stack, Typography } from "@mui/material";
import { CheckCircleOutline, PersonAddAlt1Outlined } from "@mui/icons-material";

// Components
import {
  FormikDatePicker,
  FormikTextField,
  MBSPaperCard,
} from "Common/Components";
import { QRCode } from "Components/QRCode";
import ConfirmEditModal from "Common/Modals/ConfirmEditModal/ConfirmEditModal";

// Validation
import { guestPatientValidationSchema } from "Validation";
import {
  getContactFields,
  getDemographicsFields,
  getHealthCardFields,
  guestRequiredFields,
} from "Components/PatientDrawer/patientFields";

import {
  buildGuestPatientPayload,
  DEFAULT_INITIAL_VALUES,
  mapScannedCardToInitialValues,
  mapSearchValuesToInitialValues,
} from "./formUtils";
import { styles } from "./styles";

const MBSPatientRegistration = () => {
  const { t } = useTranslation();
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmPatientValues, setConfirmPatientValues] = useState(null);
  const previousHealthCardProvinceRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();

  const initialValues = useMemo(() => {
    const { scannedCard, searchValues } = location.state || {};

    return {
      ...DEFAULT_INITIAL_VALUES,
      ...mapSearchValuesToInitialValues(searchValues),
      ...(scannedCard ? mapScannedCardToInitialValues(scannedCard) : {}),
    };
  }, [location.state]);

  const formik = useFormik({
    initialValues,
    validationSchema: guestPatientValidationSchema(t),
    onSubmit: (values, { setSubmitting }) => {
      setConfirmPatientValues(buildGuestPatientPayload(values));
      setConfirmModalOpen(true);
      setSubmitting(false);
    },
  });

  const { healthCardProvince } = formik.values;
  const { setFieldValue } = formik;
  const isManitobaHealthCard = healthCardProvince === "MB";

  useEffect(() => {
    if (
      previousHealthCardProvinceRef.current === "MB" &&
      !isManitobaHealthCard
    ) {
      setFieldValue("registrationNumber", "", false);
    }
    previousHealthCardProvinceRef.current = healthCardProvince;
  }, [healthCardProvince, isManitobaHealthCard, setFieldValue]);

  const handleQRCodeRead = (qrJSON) => {
    const scannedValues = mapScannedCardToInitialValues(qrJSON);

    formik.setValues((prev) => ({
      ...prev,
      ...scannedValues,
      healthCardProvince: "MB",
      preferredLanguage:
        scannedValues.preferredLanguage || prev.preferredLanguage,
    }));
  };

  const handleConfirm = async (createdPatient) => {
    navigate("/mbs/reason", {
      state: { patient: { ...createdPatient, inSystem: false } },
    });
  };

  const demographicsFields = useMemo(() => getDemographicsFields(t), [t]);
  const healthCardFields = useMemo(() => getHealthCardFields(t), [t]);
  const contactFields = useMemo(() => getContactFields(t), [t]);

  const fieldById = useMemo(
    () =>
      new Map(
        [...demographicsFields, ...healthCardFields, ...contactFields].map(
          (f) => [f.id, f]
        )
      ),
    [demographicsFields, healthCardFields, contactFields]
  );

  const renderField = (fieldId) => {
    const field = fieldById.get(fieldId);
    if (!field) return null;

    const fieldConfig = {
      ...field,
      required:
        field.id === "registrationNumber"
          ? isManitobaHealthCard
          : guestRequiredFields.has(field.id),
      variant: "outlined",
      disabled:
        field.id === "registrationNumber"
          ? !isManitobaHealthCard
          : field.disabled,
      onChange:
        field.id === "postalCode"
          ? (e, form) =>
              form.setFieldValue(
                "postalCode",
                (e.target.value || "").toUpperCase()
              )
          : field.onChange,
    };

    return fieldConfig.type === "date" ? (
      <FormikDatePicker key={field.id} formik={formik} field={fieldConfig} />
    ) : (
      <FormikTextField
        key={field.id}
        formik={formik}
        name={field.id}
        field={fieldConfig}
      />
    );
  };

  const renderRows = (rows) => (
    <Box sx={styles.rows}>
      {rows.map((row, i) => (
        <Stack key={i} sx={styles.row}>
          {row.map((id) => (
            <Box key={id} sx={styles.rowItem}>
              {renderField(id)}
            </Box>
          ))}
        </Stack>
      ))}
    </Box>
  );

  return (
    <Stack spacing={2} alignItems="center">
      <MBSPaperCard
        icon={<PersonAddAlt1Outlined />}
        title={t("patientForm.titleRegistration")}
        headerRight={<QRCode onSuccess={handleQRCodeRead} />}
      />
      <Stack
        component="form"
        onSubmit={formik.handleSubmit}
        spacing={2}
        alignItems="center"
      >
        <MBSPaperCard title={t("patientForm.demographics")}>
          {renderRows([
            ["firstName", "lastName", "dob"],
            ["address", "city"],
            ["postalCode", "province"],
          ])}
        </MBSPaperCard>

        <MBSPaperCard title={t("patientForm.healthCardInformation")}>
          {renderRows([
            ["healthCardProvince", "healthCardNumber", "registrationNumber"],
          ])}
        </MBSPaperCard>

        <MBSPaperCard title={t("patientForm.contactInformation")}>
          <Stack spacing={2}>
            {renderRows([
              ["preferredLanguage", "email"],
              ["primaryPhone", "secondaryPhone"],
            ])}

            <Typography variant="subtitle2">
              {t("mbsPatientRegistration.emailHelperText")}
            </Typography>
          </Stack>
        </MBSPaperCard>

        <Stack
          direction={{ xs: "column-reverse", sm: "row" }}
          spacing={1.5}
          sx={styles.actions}
        >
          <Button onClick={() => navigate("/mbs/search")} variant="text">
            {t("buttons.cancel")}
          </Button>

          <Button
            type="submit"
            disabled={formik.isSubmitting}
            variant="contained"
            startIcon={<CheckCircleOutline />}
            sx={styles.actionButton}
          >
            {formik.isSubmitting ? t("buttons.sending") : t("buttons.submit")}
          </Button>
        </Stack>
      </Stack>

      <ConfirmEditModal
        open={confirmModalOpen}
        onCloseModal={() => setConfirmModalOpen(false)}
        patientValues={confirmPatientValues || {}}
        inSystem={false}
        mode="book"
        onContinueToBooking={handleConfirm}
      />
    </Stack>
  );
};

export default MBSPatientRegistration;
